import React, { useEffect, useState } from 'react';
import { 
  Table, 
  Input, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  message, 
  Popconfirm,
  Card,
  Row,
  Col
} from 'antd';
import { 
  SearchOutlined, 
  DeleteOutlined, 
  UndoOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  fetchAllUsers,
  restoreUser,
  permanentDeleteUser,
} from '../../services/firestoreService';
import moment from 'moment';

const { Search } = Input;

const DeletedUsersManagement = () => {
  const { logActivity, isSuperAdmin } = useAdminAuth();
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDeletedUsers();
    logActivity('view_deleted_users', 'Viewed deleted users page');
  }, []);

  useEffect(() => {
    filterUsers();
  }, [deletedUsers, searchText]);

  const loadDeletedUsers = async () => {
    setLoading(true);
    try {
      const allUsers = await fetchAllUsers();
      setDeletedUsers(allUsers.filter((u) => u.status === 'deleted'));
    } catch {
      message.error('Failed to load deleted users from Firebase');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...deletedUsers];

    if (searchText) {
      filtered = filtered.filter(user => 
        user.username?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.fullName?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    setFilteredUsers(filtered);
  };

  const handleRestoreUser = async (user) => {
    try {
      await restoreUser(user.uid);
      await loadDeletedUsers();
      message.success(`User ${user.username} has been restored`);
      logActivity('restore_user', `Restored user: ${user.username}`, { userId: user.username });
    } catch {
      message.error('Failed to restore user');
    }
  };

  const handlePermanentDelete = async (user) => {
    if (!isSuperAdmin()) {
      message.error('Only Super Admins can permanently delete users');
      return;
    }

    try {
      await permanentDeleteUser(user.uid, user.username);
      await loadDeletedUsers();
      message.error(`User ${user.username} has been permanently deleted`);
      logActivity('permanent_delete_user', `Permanently deleted user: ${user.username}`, {
        userId: user.username,
      });
    } catch {
      message.error('Failed to permanently delete user');
    }
  };

  const columns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (username) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center text-white text-xs font-bold">
            {username?.charAt(0).toUpperCase()}
          </div>
          <span className="font-medium text-gray-600">{username}</span>
        </div>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email) => <span className="text-gray-600">{email || 'N/A'}</span>
    },
    {
      title: 'Subscription',
      dataIndex: 'subscriptionType',
      key: 'subscriptionType',
      render: (type) => (
        <Tag color={type === 'premium' ? 'gold' : 'default'}>
          {(type || 'free').toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Deleted At',
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      render: (date) => date ? moment(date).format('MMM DD, YYYY HH:mm') : 'N/A',
      sorter: (a, b) => new Date(b.deletedAt || 0) - new Date(a.deletedAt || 0)
    },
    {
      title: 'Registered At',
      dataIndex: 'registeredAt',
      key: 'registeredAt',
      render: (date) => date ? moment(date).format('MMM DD, YYYY') : 'N/A'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, user) => (
        <Space size="small">
          <Popconfirm
            title="Restore this user?"
            description="The user will be reactivated with active status."
            onConfirm={() => handleRestoreUser(user)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              icon={<UndoOutlined />}
              size="small"
              className="bg-green-500 hover:bg-green-600"
            >
              Restore
            </Button>
          </Popconfirm>
          
          {isSuperAdmin() && (
            <Popconfirm
              title="Permanently delete this user?"
              description="This action cannot be undone. All user data will be lost forever."
              onConfirm={() => handlePermanentDelete(user)}
              okText="Yes, Delete Forever"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              icon={<WarningOutlined className="text-red-500" />}
            >
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                size="small"
              >
                Delete Forever
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Deleted Users</h1>
          <p className="text-gray-500 mt-1">Manage soft-deleted user accounts</p>
        </div>
      </div>

      {/* Stats */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Card className="text-center bg-red-50">
            <div className="text-3xl font-bold text-red-500">{deletedUsers.length}</div>
            <div className="text-gray-600 mt-1">Deleted Users</div>
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card className="text-center bg-blue-50">
            <div className="text-3xl font-bold text-blue-500">
              {deletedUsers.filter(u => u.subscriptionType === 'premium').length}
            </div>
            <div className="text-gray-600 mt-1">Premium Users</div>
          </Card>
        </Col>
      </Row>

      {/* Search */}
      <Card>
        <Search
          placeholder="Search by username, email, or name..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: '100%', maxWidth: 400 }}
          allowClear
        />
      </Card>

      {/* Warning Message */}
      <Card className="bg-yellow-50 border-yellow-200">
        <div className="flex items-start gap-3">
          <WarningOutlined className="text-yellow-600 text-xl mt-1" />
          <div>
            <h3 className="font-semibold text-yellow-800">Important Notice</h3>
            <p className="text-yellow-700 text-sm mt-1">
              Deleted users are hidden from the main user list but their data is preserved. 
              You can restore them or permanently delete them. 
              {isSuperAdmin() ? ' As a Super Admin, you have permission to permanently delete users.' : ' Only Super Admins can permanently delete users.'}
            </p>
          </div>
        </div>
      </Card>

      {/* Deleted Users Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="uid"
          loading={loading}
          pagination={{ 
            pageSize: 10, 
            showSizeChanger: true, 
            showTotal: (total) => `Total ${total} deleted users` 
          }}
          locale={{ emptyText: 'No deleted users found' }}
        />
      </Card>
    </div>
  );
};

export default DeletedUsersManagement;
